import { raiseErrorToast } from '@celito.clients/utils';
import { hashQueryKey, useQuery } from 'react-query';

import { RulesComponentListViewFilter } from '../../rules-component/types/rules-component.types';
import { getSelectedOptions, IData } from './selected-options';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export interface UseSelectedOptionsQueryParams {
  objectName?: string;
  selectedFilter: RulesComponentListViewFilter;
  fetchSelectedOptions?: boolean;
  selectedValuesCount: number;
  shouldFetch: boolean;
}

// react-query v3 invokes each observer's `onError` independently, so two
// pickers sharing the same query key would otherwise raise two toasts for
// one failure. Dedupe per query hash instead (AC3: exactly 1 toast/key).
// The hash is cleared whenever a new fetch STARTS for that key (queryFn
// invocation), not just on success — an errored query refetches on
// observer remount (retryOnMount defaults true even with
// refetchOnMount: false), so clearing only on success would permanently
// swallow toasts for a key that keeps failing across remounts.
const toastedQueryHashes = new Set<string>();

export const useSelectedOptionsQuery = ({
  objectName,
  selectedFilter,
  fetchSelectedOptions,
  selectedValuesCount,
  shouldFetch,
}: UseSelectedOptionsQueryParams) => {
  const queryKey = ['selected-options', objectName, selectedFilter];
  const queryHash = hashQueryKey(queryKey);

  return useQuery<IData>({
    queryKey,
    queryFn: () => {
      toastedQueryHashes.delete(queryHash);
      return getSelectedOptions(
        objectName as string,
        selectedFilter,
        fetchSelectedOptions,
        selectedValuesCount
      );
    },
    enabled: Boolean(objectName) && shouldFetch,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: FIVE_MINUTES_MS,
    onError: (error) => {
      if (toastedQueryHashes.has(queryHash)) return;
      toastedQueryHashes.add(queryHash);
      raiseErrorToast(error);
    },
  });
};
